import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchComponent from '../SearchComponent';
import SelectResidual from '../SelectResidual';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

/**
 * RegressionAccordion Component
 */
const RegressionAccordion = ({
  index,
  returnStream,
  handleDataChange,
  onRemove,
  availableDescriptions,
  data,
  baseUrl,
  includeMsci = false,
  searchProviders = null,
}) => {
  // Local state
  const [desc, setDesc] = useState(returnStream.description);
  const [expanded, setExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(returnStream.source || '');
  const [residuals, setResiduals] = useState(returnStream.residualization || []);
  const [chartData, setChartData] = useState(null);
  const [regStats, setRegStats] = useState(null);
  const [loadingViz, setLoadingViz] = useState(false);
  const [vizError, setVizError] = useState('');

  // Sync props → state
  useEffect(() => {
    setDesc(returnStream.description);
    setSelectedIndex(returnStream.source || '');
    setResiduals(returnStream.residualization || []);
  }, [returnStream]);

  // Handlers for expand/collapse
  const handleChange = (_, isExpanded) => setExpanded(isExpanded);

  // Primary stream selection
  const handleSelectIndex = src => {
    setSelectedIndex(src);
    handleDataChange(index, 'source', src);
  };

  // Add/remove residual factors
  const handleAddResidual = r => {
    const next = [...residuals, r];
    setResiduals(next);
    handleDataChange(index, 'residualization', next);
  };
  const handleDeleteResidual = r => {
    const next = residuals.filter(x => x !== r);
    setResiduals(next);
    handleDataChange(index, 'residualization', next);
  };

  // Description blur → update
  const handleBlur = () => handleDataChange(index, 'description', desc);

  // Fetch & build chart data
  const handleVisualize = async () => {
    setLoadingViz(true);
    setVizError('');
    try {
      const payloadData = {
        ...data,
        residual_return_streams: data.residual_return_streams.map((stream, streamIdx) =>
          streamIdx === index
            ? {
                ...stream,
                description: desc,
                source: selectedIndex,
                residualization: residuals,
              }
            : stream
        ),
      };
      const payload = { targetDescription: desc, data: payloadData };

      const res = await fetch(`${baseUrl}/api/calc-residual-return-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const rawText = await res.text();
      let json = {};
      try {
        json = rawText ? JSON.parse(rawText) : {};
      } catch {
        throw new Error('Residual analysis API returned invalid JSON.');
      }

      if (!res.ok) {
        throw new Error(json.error || `Residual analysis failed (${res.status}).`);
      }
      if (json.error) {
        throw new Error(json.error);
      }
      if (!Array.isArray(json.datasets) || !Array.isArray(json.labels)) {
        throw new Error('Residual analysis API returned an unexpected response shape.');
      }

      // Map each returned series → line or bar
      const datasets = json.datasets.map((d, i) => {
        const isResidual = Boolean(d.isResidual);
        return {
          label: d.label,
          data: d.data,
          type: isResidual ? 'bar' : 'line',
          fill: false,
          borderColor: isResidual
            ? 'rgba(75,192,192,1)'
            : getDatasetColor(i),
          backgroundColor: isResidual
            ? 'rgba(75,192,192,0.4)'
            : getDatasetColor(i, 0.25),
          borderWidth: isResidual ? 1 : i === 0 ? 3 : 2,
          pointRadius: 0,
          tension: 0.3,
        };
      });

      setChartData({ labels: json.labels, datasets });
      setRegStats(json.regression_stats || {});
    } catch (err) {
      console.error('Visualization error:', err);
      setVizError(err.message || 'Unable to generate residual analysis.');
    } finally {
      setLoadingViz(false);
    }
  };

  // Helper: color palette
  const getDatasetColor = (idx, alpha = 1) => {
    const palette = [
      'rgba(54,162,235,1)',
      'rgba(255,99,132,1)',
      'rgba(255,159,64,1)',
      'rgba(201,203,207,1)',
      'rgba(255,205,86,1)',
      'rgba(153,102,255,1)',
    ];
    return palette[idx % palette.length].replace(/1\)$/, `${alpha})`);
  };

  // Chart.js options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        title: { display: true, text: 'Date', font: { weight: 'bold' } },
        grid: { display: false },
      },
      y: {
        title: { display: true, text: 'Return', font: { weight: 'bold' } },
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { callback: v => `${(v * 100).toFixed(1)}%` },
      },
    },
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: ctx => {
            const v = ctx.parsed.y;
            return `${ctx.dataset.label}: ${(v * 100).toFixed(2)}%`;
          },
        },
      },
      title: {
        display: true,
        text: `${desc} — Raw, Predicted & Residual`,
        font: { size: 16, weight: 'bold' },
      },
    },
  };

  // Tooltip for residual explanation
  const ResidualInfo = () => (
    <Tooltip title={
      <Typography sx={{ p: 1 }}>
        The final return stream is the 'residual' of using the selected return streams to predict the primary return stream.
      </Typography>
    }>
      <InfoOutlinedIcon fontSize="small" color="info" />
    </Tooltip>
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      sx={{
        borderRadius: 2,
        mb: 2,
        boxShadow: expanded
          ? '0 4px 12px rgba(0,0,0,0.1)'
          : '0 2px 6px rgba(0,0,0,0.08)',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 64, '&.Mui-expanded': { bgcolor: 'rgba(0,0,0,0.02)' } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            sx={{ width: `${Math.max(desc.length + 2, 15)}ch`, minWidth: 150 }}
            onClick={e => e.stopPropagation()}
          />

          {selectedIndex && <Chip label={selectedIndex} color="primary" size="small" />}

          {residuals.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Residualized Against
                </Typography>
                <ResidualInfo />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {residuals.map(r => (
                  <Chip
                    key={r}
                    label={r}
                    size="small"
                    onDelete={e => {
                      e.stopPropagation();
                      handleDeleteResidual(r);
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {chartData && <VisibilityIcon color="success" />}
          <IconButton
            onClick={e => {
              e.stopPropagation();
              onRemove(desc);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Configuration Panel */}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              Configure Return Stream
            </Typography>
            <Box sx={{ mb: 2 }}>
            <Typography variant="body2">Primary Return Stream:</Typography>
              <SearchComponent
                onSelect={handleSelectIndex}
                baseUrl={baseUrl}
                includeMsci={includeMsci}
                searchProviders={searchProviders}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Add Residual Factors: <ResidualInfo />
              </Typography>
              <SelectResidual onSelect={handleAddResidual} availableDescriptions={availableDescriptions} />
            </Box>
          </Paper>

          {/* Visualize Button */}
          <Box>
            <Button
              variant="contained"
              startIcon={loadingViz && <CircularProgress size={16} />}
              onClick={handleVisualize}
              disabled={loadingViz || !selectedIndex}
            >
              {loadingViz ? 'Calculating…' : 'Show Residual Analysis'}
            </Button>
            {vizError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {vizError}
              </Typography>
            )}
          </Box>

          {/* Chart + Stats */}
          {chartData && (
            <Paper sx={{ p: 2 }} variant="outlined">
              <Box sx={{ height: 360, mb: 2 }}>
                <Line data={chartData} options={chartOptions} />
              </Box>

              {regStats && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Regression Statistics
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 480 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>R²</TableCell>
                          <TableCell align="right">{regStats.r_squared?.toFixed(3)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Adj&nbsp;R²</TableCell>
                          <TableCell align="right">{regStats.adj_r_squared?.toFixed(3)}</TableCell>
                        </TableRow>
                        {regStats.coefficients &&
                          Object.entries(regStats.coefficients).map(([k, v]) => (
                            <TableRow key={k}>
                              <TableCell>{k}</TableCell>
                              <TableCell align="right">
                                {v.toFixed(4)}
                                {regStats.p_values && regStats.p_values[k] != null && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ color: 'text.secondary', ml: 1 }}
                                  >
                                    (p = {regStats.p_values[k].toFixed(3)})
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Paper>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default RegressionAccordion;
